import React, {
  useCallback,
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { Bank, TransferData, TransferType } from "../@types";
import { APIError, APIResponse } from "../api";
import useBankTransfer from "../hooks/useBankTransfer";
import useVisaroTransfer from "../hooks/useVisaroTransfer";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

export type Transfer = {
  bank?: Bank;
  recipientName?: string;
  accountNumber?: string;
  amount?: string;
  transferType?: TransferType;
  note?: string;
  username?: string;
};

interface SendContextProps {
  transfer: Transfer | null;
  setTransfer: React.Dispatch<React.SetStateAction<Transfer | null>>;
  makeTransfer: (pin: string) => void;
  transfering: boolean;
  transferError: APIError | null;
  isTransferError: boolean;
  transferData: APIResponse<TransferData> | undefined;
}

const SendContext = createContext<SendContextProps | undefined>(undefined);

export function useSendContext() {
  const context = useContext(SendContext);
  if (!context) {
    throw new Error("useSendContext must be used within a SendProvider");
  }
  return context;
}

const SendContextProvider: FC<
  PropsWithChildren<{ defaultContextValue?: Partial<Transfer> }>
> = ({ children, defaultContextValue }) => {
  const [transfer, setTransfer] = useState<Transfer | null>(
    defaultContextValue ? { ...defaultContextValue } : null
  );

  const { refetch } = useAuth();

  const { transfer: bankTransferMutation, ...bank } = useBankTransfer({
    onSettled() {
      refetch();
    },
    onError() {
      toast.error("An error occurred while making transfer");
    },
  });

  const { transfer: visaroTransferMutation, ...visaro } = useVisaroTransfer({
    onSettled() {
      refetch();
    },
    onError() {
      toast.error("An error occurred while making transfer");
    },
  });

  const makeTransfer = useCallback(
    (pin: string) => {
      if (transfer?.amount && transfer.note && transfer.transferType) {
        if (
          transfer?.transferType === "inter" &&
          transfer.accountNumber &&
          transfer.bank &&
          transfer.recipientName
        ) {
          bankTransferMutation({
            Amount: parseInt(transfer.amount),
            ReceiverAccountNumber: transfer.accountNumber,
            ReceiverBankCode: transfer.bank.code,
            ReceiverName: transfer.recipientName,
            Narration: transfer.note,
            pin,
          });
        } else if (transfer.username) {
          visaroTransferMutation({
            amount: transfer.amount,
            note: transfer.note,
            pin,
            recipient_username: transfer.username,
          });
        }
      }
    },
    [transfer, bankTransferMutation, visaroTransferMutation]
  );

  const transferProcessDetails = useMemo(() => {
    return transfer?.transferType === "inter" ? bank : visaro;
  }, [transfer, bank, visaro]);

  return (
    <SendContext.Provider
      value={{
        makeTransfer,
        transfer,
        setTransfer,
        ...transferProcessDetails,
      }}
    >
      {children}
    </SendContext.Provider>
  );
};

export default SendContextProvider;
